<template>
  <div class="subscription-landing subscription-landing--wadiscovery">
    <section class="hero">
      <div class="site-width">
        <div class="hero-content">
          <h1 class="clubname">Discovery Club</h1>
          <p class="club-subhead">
            A Masterclass in
            <br />
            World-Class Wine
          </p>
          <p class="description">
            Expand your palate—and your knowledge—with quarterly deliveries of
            stunning, expert-selected wines.
          </p>

          <div class="button-link">
            <p>
              <a v-scroll-to="'#club-choice'" href="#" class="button primary">
                <span class="button-inset"> Become a Member </span>
              </a>
            </p>
            <p>
              <span class="gift-alternative">
                <nuxt-link
                  to="/wine-club/gifting/discovery-club/"
                  class="button"
                >
                  Or gift it
                </nuxt-link>
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>
    <section class="pad-reduced">
      <div class="site-width text-center">
        <p class="reduced section-heading">Top Pick</p>
        <figure class="quote">
          <img
            src="~assets/images/logo-nytw.png"
            alt="The New York Times Wirecutter Logo"
          />
          <blockquote>
            “This club immediately stood out for its transparency about the
            selection process, range of wines, educational materials, and free
            temperature-controlled shipping (a rarity among most subscription
            services)[...] Among the wine clubs we tested, Wine Access’s
            customer service was also the best, and this club ships to the most
            states.”
          </blockquote>
        </figure>
      </div>
    </section>
    <section class="shaded pad-zero cork-offset">
      <div class="bottle-right">
        <div class="site-width text-center">
          <div class="cork-left">
            <img
              src="~assets/images/club-discovery-cork.png"
              class="mobile-cork"
              alt="Cork"
            />
            <div>
              <h2 class="benefits">Member Benefits</h2>
              <p>
                Each wine is hand-selected by a team of industry experts,
                including a Master Sommelier, International Wine Judge and other
                industry experts.
              </p>
            </div>
            <div class="row icon-grid">
              <div class="columns medium-6 large-3">
                <div class="icon">
                  <img
                    src="~assets/images/wa-icon-club-discount.svg"
                    alt="Percent Symbol"
                    class="icon-desktop"
                  />
                  <img
                    src="~assets/images/wa-icon-club-discount-mobile.svg"
                    alt="Percent Symbol"
                    class="icon-mobile"
                  />
                </div>
                <p>10% off all Wine Access purchases *</p>
              </div>
              <div class="columns medium-6 large-3">
                <div class="icon">
                  <img
                    src="~assets/images/wa-icon-club-video.svg"
                    alt="Video Camera Icon"
                    class="icon-desktop"
                  />
                  <img
                    src="~assets/images/wa-icon-club-video-mobile.svg"
                    alt="Video Camera Icon"
                    class="icon-mobile"
                  />
                </div>
                <p>Exclusive video tastings</p>
              </div>
              <div class="columns medium-6 large-3">
                <div class="icon">
                  <img
                    src="~assets/images/wa-icon-club-bottle.svg"
                    alt="Wine Glass Icon"
                    class="icon-desktop"
                  />
                  <img
                    src="~assets/images/wa-icon-club-bottle-mobile.svg"
                    alt="Wine Glass Icon"
                    class="icon-mobile"
                  />
                </div>
                <p>Access to rare wines</p>
              </div>
              <div class="columns medium-6 large-3">
                <div class="icon">
                  <img
                    src="~assets/images/wa-icon-club-shipping.svg"
                    alt="Box Icon"
                    class="icon-desktop"
                  />
                  <img
                    src="~assets/images/wa-icon-club-shipping-mobile.svg"
                    alt="Box Icon"
                    class="icon-mobile"
                  />
                </div>
                <p>Complimentary shipping</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section id="club-choice">
      <div class="site-width text-center">
        <BreadcrumbComponent :breadcrumbs="breadcrumbs" />
        <div>
          <p class="section-heading">
            Travel the Globe and Say
            <br />
            Goodbye to Guesswork
          </p>
          <p>
            Enjoy exclusive access to rare, distinctive wines, intentionally
            grouped by theme to broaden your knowledge of the fascinating world
            of wine. Whether you’re exploring high-altitude grapes, old-world
            vines, or a taste of the next generation of vintners, you’ll find
            compelling classic and new-wave selections from diverse regions
            worldwide—and unparalleled tasting experiences.
          </p>
        </div>
        <div>
          <div class="bottle-selection" :class="[selectedBottleType]">
            <div class="bottle"></div>
            <div class="bottle"></div>
            <div class="bottle"></div>
            <div class="bottle"></div>
            <div class="bottle"></div>
            <div class="bottle"></div>
          </div>
          <div class="club-selector">
            <div class="option">
              <input
                id="discovery-club-all-reds"
                v-model="selectedPlan"
                value="discovery-club-all-reds"
                type="radio"
              />
              <label for="discovery-club-all-reds">All Red</label>
            </div>
            <div class="option">
              <input
                id="discovery-club-all-whites"
                v-model="selectedPlan"
                value="discovery-club-all-whites"
                type="radio"
              />
              <label for="discovery-club-all-whites">All White</label>
            </div>
            <div class="option">
              <input
                id="discovery-club-mixed"
                v-model="selectedPlan"
                value="discovery-club-mixed"
                type="radio"
              />
              <label for="discovery-club-mixed">Mixed</label>
            </div>
          </div>
          <div class="bordered-callout">
            <p>
              Exclusive access to 6 rare, distinctive bottles, delivered
              quarterly – $150 per shipment
            </p>
          </div>
        </div>
        <p>
          <a v-if="hasMembership" href="#" class="button disabled primary"
            >Currently Enrolled</a
          >
          <a v-else href="#" class="button primary" @click.prevent="signup()"
            >Become a Member</a
          >
        </p>
        <p>
          <span class="gift-alternative">
            <nuxt-link to="/wine-club/gifting/discovery-club/" class="button">
              Or gift it
            </nuxt-link>
          </span>
        </p>
      </div>
    </section>
    <section class="shaded">
      <div class="site-width">
        <div class="row xlarge-flex">
          <div
            class="columns large-6 text-center side-image hide-for-medium-down"
          >
            <img
              src="~assets/images/club-discovery-glass.png"
              class="wineglass"
              alt="Raised glass of wine"
            />
          </div>
          <div class="columns large-6 text-center large-text-left">
            <p class="max-copy section-heading">
              The Diverse World of
              <span class="nobr">Wine—</span>
              <br class="hide-for-large-up" />Directly to Your Door
            </p>
            <p>
              <strong
                >Stunning wines and storied histories you won’t discover
                anywhere else. Drink up the fascinating stories that come with
                them.</strong
              >
            </p>
            <p>
              There’s no question that a glass of excellent wine will change
              your evening. But learning about what makes that wine excellent
              elevates your entire experience. When you join The Discovery Club,
              we’ll send you the remarkable wines you need to try—and teach you
              everything you need to know about what you’re tasting. Become
              fluent in tannins, texture, and terroir with quarterly shipments
              of six bottles of red, white, or both, hand-selected to broaden
              your palate and your perspective. Whether you’re learning about
              how soil type and altitude impact your sip or you’re cultivating a
              newfound love of new-wave wines, one thing is certain. You’ll
              taste it all. Then, you’ll feel it all. There’s a reason The New
              York Times Wirecutter named us as their #1 wine club.
            </p>
            <p>
              <a v-scroll-to="'#club-choice'" href="#" class="button primary"
                >Join the Club</a
              >
            </p>
          </div>
        </div>
      </div>
    </section>
    <section class="pale-green pad-zero">
      <div class="club-hands-container">
        <div class="hand-left" />
        <div class="quote-carousel-container">
          <h2>What Our Club Members are Saying</h2>
          <ClientOnly>
            <Carousel v-bind="carouselOptions">
              <Slide v-for="(quote, index) in quotes" :key="index">
                <figure>
                  <blockquote>
                    {{ quote.text }}
                  </blockquote>
                  <figcaption>— {{ quote.source }}</figcaption>
                </figure>
              </Slide>
              <template #addons>
                <Navigation />
                <Pagination />
              </template>
            </Carousel>
          </ClientOnly>
        </div>
        <div class="hand-right" />
      </div>
    </section>
    <ExpertsSection extra-classes="shaded" />
    <section class="text-center">
      <div class="site-width text-center">
        <div>
          <p class="mb-40 section-heading">
            Taste the Journey
            <br />
            of a Lifetime
          </p>

          <div class="button-link">
            <p>
              <a v-scroll-to="'#club-choice'" href="#" class="button primary">
                <span class="button-inset"> Become a Member Today </span>
              </a>
            </p>
            <p>
              <span class="gift-alternative">
                <nuxt-link
                  to="/wine-club/gifting/discovery-club/"
                  class="button"
                >
                  Or gift it
                </nuxt-link>
              </span>
            </p>
          </div>
        </div>
        <div class="content-hero">
          <img
            src="~assets/images/club-discovery-group.jpg"
            alt="People around a table raising wine glasses"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ga4 from '#common/utils/ga4'
import {
  determineDefaultSelectedPlan,
  getClubCrumbs,
  hasMembershipForPlan,
} from '#common/utils/wineclub'

export default {
  name: 'DiscoveryClubIndex',
  components: {
    BreadcrumbComponent: defineAsyncComponent(
      () => import('@/components/Breadcrumb.vue'),
    ),
    ExpertsSection: defineAsyncComponent(
      () => import('@/components/Club/ExpertsSection'),
    ),
  },
  data() {
    return {
      club: {},
      memberships: [],
      selectedPlan: '',
      modalTheme: {
        title: 'Welcome to the Club',
        container: {
          classes: 'wineclub-form',
        },
        login: {
          submitButton: {
            classes: 'primary continue',
          },
        },
        register: {
          submitButton: {
            classes: 'primary continue',
            label: 'Continue',
          },
        },
        emailCheck: {
          submitButton: {
            classes: 'primary continue',
            label: 'Continue',
          },
        },
      },
      carouselOptions: {
        autoplay: 10000,
        pauseAutoplayOnHover: true,
        wrapAround: true,
        itemsToShow: 1,
      },
      quotes: [
        {
          text:
            'I love my Wine Access wine club and the info/availability on your website. I learn so much and ' +
            "love the wines. It's a highlight in my life receiving shipments from you all 😀",
          source: 'Penney G.',
        },
        {
          text: 'It gives me insight into new wines I would never think to drink. If I find something I like I like the ability to be able to purchase more.',
          source: 'Robert T.',
        },
        {
          text: "Really impressed with the quality of the wines. The wine club selections are wines that I normally would not select on my own, but I've enjoyed every single one so far.",
          source: 'Brett C.',
        },
        {
          text: 'Great service, great wine, and selections that keep me learning in the wine club selections. Love Wine Access!',
          source: 'Shawn Y.',
        },
        {
          text: 'I love the selection, tasting notes, and videos. I’ve even built a wine club with friends I had sign up for the club with me.',
          source: 'Damona H.',
        },
        {
          text: 'Only wine club I’ve ever stuck with. Great value for wines, excellent customer service, great selection.. what’s not to like!',
          source: 'Joni P.',
        },
      ],
      breadcrumbs: getClubCrumbs('Discovery Wine Club'),
    }
  },
  async fetch() {
    const resp = await this.$axios.get('/api/membership/list/')
    this.club = resp.data.results.find((c) => c.slug === 'discovery-club')
  },
  computed: {
    loggedIn() {
      return this.$auth.loggedIn
    },
    selectedBottleType() {
      if (this.selectedPlan === 'discovery-club-mixed') return 'mixed'
      if (this.selectedPlan === 'discovery-club-all-whites') return 'white'
      return 'red'
    },
    hasMembership() {
      let hasMembership = false
      this.memberships.forEach((m) => {
        hasMembership = m.plans.some((p) => p.is_member)
      })
      return hasMembership
    },
  },
  watch: {
    async loggedIn(newValue, oldValue) {
      // Once user data is loaded and we know they're logged in, fetch their memberships
      if (newValue === true && oldValue === false) {
        await this.fetchMemberships()
      }
    },
  },
  mounted() {
    this.setup()
  },
  activated() {
    this.setup()
  },
  methods: {
    async fetchMemberships() {
      const result = await this.$axios.get('/api/membership/')
      this.memberships = result.data.memberships.filter(
        (m) => m.slug === 'discovery-club',
      )
      // If a plan wasn't selected or they have a membership for a plan that was selected previous to login
      if (
        !this.$store.getters.wineClubPreselectedPlan ||
        hasMembershipForPlan(
          this.$store.getters.wineClubPreselectedPlan,
          this.memberships,
        )
      ) {
        // Set the appropriate pre-selected plan based on which clubs they're already a member of
        const defaultPlan = determineDefaultSelectedPlan(this.memberships)
        this.setPreselectedPlan(defaultPlan)
      }
    },
    setup() {
      // Default to all red
      this.setPreselectedPlan('discovery-club-all-reds')
      // clear memberships and if they're logged in, fetch
      this.memberships = []
      if (this.loggedIn) {
        this.fetchMemberships()
      }
      ga4.trackViewClub({ slug: 'discovery-club' })
    },
    ctaText(text) {
      if (
        this.club.plans.some((p) => p.slug === this.selectedPlan && p.is_full)
      ) {
        return 'Join The Waiting List'
      } else {
        return text
      }
    },
    openLoginModal() {
      this.$auth.openAuthModal({
        theme: this.modalTheme,
        redirect: '/discovery-wine-club/join/',
        isClub: true,
      })
    },
    setPreselectedPlan(planSlug) {
      this.selectedPlan = planSlug
      this.$store.dispatch('setWineClubPreselectedPlan', planSlug)
    },
    signup() {
      this.$store.dispatch('setWineClubPreselectedPlan', this.selectedPlan)
      if (!this.$auth.loggedIn) {
        this.openLoginModal()
      } else {
        this.$router.push('/discovery-wine-club/join/')
      }
    },
  },
}
</script>
